// Live's Tweak (#678): the Studio card's Tweak and the preview's "Change this image" open the
// composer on a delivered image of an earlier job (`?type=image.tweak&job=&output=`). Live's
// Tweak form (`pages/images/components/generation-form/tweak-form`, read for behavior) changes
// that one image into one: it has no product, reference or template tools, its count is 1, and
// its models are the ad-copy rows (its settings' `isAdvanced`). Its request sends the image first,
// then the prompt inside live's wrapper (`edit: "tweak"`, G6-studio-api). A library file or a
// product as the source is live's image to image instead (the library's Edit, #233): no tweak.
// Its ratio starts at the tweaked image's own and is sent (`tweakRatio`).
import type { Job } from "../../../api/generated/model";
import type { GenerateDraft } from "../draft-state";

/** An image change of a delivered output: live's Tweak, not its image to image. */
export function isTweak(draft: Pick<GenerateDraft, "type" | "source">): boolean {
  return draft.type === "image.tweak" && draft.source?.kind === "job_output";
}

/**
 * The draft as live's Tweak form holds it: the one image and one output. The draft underneath
 * keeps its own count, so removing the image gives the composer's back.
 */
export function asTweak(draft: GenerateDraft): GenerateDraft {
  return isTweak(draft) ? { ...draft, outputs: 1, references: [] } : draft;
}

/**
 * The tweaked image's own ratio, where live's Tweak form starts its select
 * (`tweakedImage.aspectRatio`): the earlier job's ratio when the model offers it, else the
 * offered ratio nearest the output's shape (a change that kept its source's shape names none).
 * Null until the job is read, or with no shape to go by: the server then keeps the image's.
 */
export function tweakRatio(
  job: Job | undefined,
  outputIndex: number,
  offered: readonly string[],
): string | null {
  if (job === undefined) return null;
  const named = job.params?.aspect_ratio ?? null;
  if (named !== null && offered.includes(named)) return named;
  const output = job.outputs.find((each) => each.index === outputIndex);
  if (output?.width == null || output.height == null || output.height === 0) return null;
  const shape = output.width / output.height;
  let nearest: string | null = null;
  let gap = Infinity;
  for (const value of offered) {
    const [width, height] = value.split(":").map(Number);
    if (width === undefined || height === undefined || !(width > 0) || !(height > 0)) continue;
    const off = Math.abs(Math.log(width / height / shape));
    if (off < gap) {
      gap = off;
      nearest = value;
    }
  }
  return nearest;
}
